import React, { useEffect, useState } from 'react';
import { Loader2, Save, PanelLeft } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { CONFIGURABLE_SIDEBAR_ITEMS } from '@/lib/sidebarNav';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

/**
 * s-admin: show/hide individual left-sidebar items for one tenant.
 * Checked = visible. Unchecked = hidden via tenant.sidebar_menu.hidden.
 */
export default function SidebarMenuDialog({ tenant, onClose, onSaved }) {
  const [hidden, setHidden] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setLoading(true);
    api.get(`/tenants/${tenant.id}/sidebar-menu`)
      .then((r) => setHidden(new Set(r.data?.hidden || [])))
      .catch((e) => toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }, [tenant]);

  const isVisible = (id) => !hidden.has(id);

  const toggle = (id, visible) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setAll = (visible) => {
    if (visible) {
      setHidden(new Set());
    } else {
      setHidden(new Set(CONFIGURABLE_SIDEBAR_ITEMS.map((i) => i.id)));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.id}/sidebar-menu`, {
        hidden: Array.from(hidden),
      });
      toast.success('Seitenmenü gespeichert');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const visibleCount = CONFIGURABLE_SIDEBAR_ITEMS.length - hidden.size;

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PanelLeft className="w-5 h-5 text-brand-red" />
            Seitenmenü – {tenant?.name}
          </DialogTitle>
          <DialogDescription>
            Ausgeblendete Punkte erscheinen in der linken Leiste nicht (für alle Nutzer dieses Mandanten).
            Super-Admin-Menüpunkte bleiben immer sichtbar.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline" /> Lade…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100">
              <p className="text-xs text-slate-500">
                {visibleCount} von {CONFIGURABLE_SIDEBAR_ITEMS.length} sichtbar
              </p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => setAll(true)}>
                  Alle an
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setAll(false)}>
                  Alle aus
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 space-y-1 py-2 pr-1" data-testid="sidebar-menu-list">
              {CONFIGURABLE_SIDEBAR_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-slate-50"
                >
                  <Label
                    htmlFor={`sidebar-toggle-${item.id}`}
                    className="text-sm font-medium text-slate-700 cursor-pointer flex-1"
                  >
                    {item.name}
                  </Label>
                  <Switch
                    id={`sidebar-toggle-${item.id}`}
                    checked={isVisible(item.id)}
                    onCheckedChange={(v) => toggle(item.id, v)}
                    data-testid={`sidebar-toggle-${tenant?.slug}-${item.id}`}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={loading || saving}
            data-testid={`sidebar-menu-save-${tenant?.slug}`}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
